import { CacheItem } from './CurrentPage/cache';

type ThemeContextCreator = () => any;
type ThemeContext = any;
const loadedComponents: { [name: string]: React.ComponentType<any> } = {};
const loadedThemeContexts: { [name: string]: ThemeContext } = {};

type ComponentRegistry = {
  [name: string]: (theme?: string) => Promise<{ component: React.ComponentType<any>; theme: string }>;
};
let componentRegistry: ComponentRegistry;

export function setComponentRegistry(registry: ComponentRegistry) {
  componentRegistry = registry;
}

type ThemeContextCreatorRegistry = { [name: string]: () => Promise<ThemeContextCreator> };
let themeContextCreatorRegistry: ThemeContextCreatorRegistry;

export function setThemeContextCreatorRegistry(registry: ThemeContextCreatorRegistry) {
  themeContextCreatorRegistry = registry;
}

export function componentHasLoaded(componentName: string, component: React.ComponentType<any>, theme?: string) {
  if (theme) {
    componentName += ':' + theme;
  }
  loadedComponents[componentName] = component;
}

function hasComponentLoaded(componentName: string, theme?: string) {
  if (theme) {
    componentName += ':' + theme;
  }
  return !!loadedComponents[componentName];
}

export function themeContextHasLoaded(themeName: string, contextCreator: ThemeContextCreator) {
  loadedThemeContexts[themeName] = contextCreator();
  return loadedThemeContexts[themeName];
}

function resolveComponent(componentName: string, theme?: string) {
  const componentLoader = componentRegistry[componentName];
  if (!componentLoader) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        "Component '" +
          componentName +
          "' does not exist. " +
          'Did you forget to build the frontend or run scope.cmd frontend:build-component-registry?' +
          'It could also be that the component file is missing a correct @' +
          'ComponentFor notation',
      );
    } else {
      return Promise.reject(new Error("Component '" + componentName + "' does not exist."));
    }
  }
  return componentLoader(theme).then(
    component => {
      if (!component.component) {
        if (process.env.NODE_ENV !== 'production') {
          throw new Error(
            "Component '" +
              componentName +
              "' could not be loaded. " +
              'Did you forget to default export your component in your file?',
          );
        }
      }
      componentHasLoaded(componentName, component.component, component.theme);
      return component;
    },
    e => {
      console.error(e);
      const err: BundleLoadError = new Error('Failed to load component: ' + componentName) as any;
      err.additionalChunkLoadError = e || true;
      return Promise.reject(err);
    },
  );
}

export function resolveThemeContext(themeName: string) {
  if (loadedThemeContexts[themeName]) {
    return Promise.resolve(loadedThemeContexts[themeName]);
  }

  const getContextCreator = themeContextCreatorRegistry[themeName];
  if (!getContextCreator) {
    return Promise.resolve();
  }
  return getContextCreator().then(contextCreator => {
    return themeContextHasLoaded(themeName, contextCreator);
  });
}

export function resolveThemeContextSyncIfLoadedOrThrow(themeName: string) {
  const context = loadedThemeContexts[themeName];
  if (!context) {
    throw new Error(
      'The theme context ' + themeName + ' has not been loaded. It needs to be loaded before trying to use it.',
    );
  }
  return context;
}

export function allComponentsAreResolved<TData>(data: TData, theme?: string) {
  const componentNames = findAllComponentNames(data);
  const notYetLoadedComponents = componentNames.filter(c => !hasComponentLoaded(c, theme));
  return notYetLoadedComponents.length === 0;
}

export function resolveComponentAndChildComponents<TData>(data: TData, theme?: string) {
  const componentNames = findAllComponentNames(data);
  if (!componentNames.length) {
    return Promise.resolve(data);
  }

  return Promise.all(componentNames.map(componentName => resolveComponent(componentName, theme))).then(
    components => {
      return data;
    },
    e => {
      console.error(e);
      const err: BundleLoadError = new Error('Failed to load additional chunks') as any;
      err.additionalChunkLoadError = e || true;
      return Promise.reject(err);
    },
  );
}

function findAllComponentNames(object: any, isRoot = true, componentNames: string[] = []) {
  if (!object) {
    return componentNames;
  }
  Object.keys(object).forEach(key => {
    if (key === 'componentName') {
      const cacheItem = object as CacheItem;
      // We only load partials if it's on the root object because that component is needed to render
      // the root. A deep partial has a component name so that you can navigate to it quickly, but it
      // isn't used to render the root.
      if (!componentNames.includes(cacheItem.componentName) && (isRoot || cacheItem.$cache !== 'partial')) {
        componentNames.push(cacheItem.componentName);
      }
    } else if (typeof object[key] === 'object') {
      findAllComponentNames(object[key], false, componentNames);
    } else if (Array.isArray(object[key])) {
      (object[key] as Object[]).forEach(o => {
        findAllComponentNames(o, false, componentNames);
      });
    }
  });
  return componentNames;
}

export function resolveSyncIfLoadedOrThrow(componentName: string, theme?: string) {
  const componentNameWithTheme = theme ? componentName + ':' + theme : componentName;
  let component = loadedComponents[componentNameWithTheme];
  if (!component) {
    component = loadedComponents[componentName];
    if (!component) {
      console.error('Loaded components:', loadedComponents);
      throw new Error(
        'The component ' + componentName + ' has not been loaded. It needs to be loaded before trying to render it.',
      );
    }
  }
  return component;
}

export type BundleLoadError = Error & {
  additionalChunkLoadError: any;
};

export function isBundleLoadError(e: Object): e is BundleLoadError {
  const loadError = e as BundleLoadError;
  return !!loadError.additionalChunkLoadError;
}
